import { events, trackEvent, trackGroup } from '../analytics';

import {
  ExperimentationProvider as OptimizelyExperimentationProvider,
  onExperimentActivated,
  onExperimentVariationViewed,
} from './vendors/optimizely';

// a private in-memory tracker to prevent duplicate events from being sent
// Data confirms that it is OKAY to send duplicate events on app restart,
// so we do not need to persist this data
const _experimentVariationViewedTracker = {};
// a private in-memory map of active experiments. This helps us prevent
// sending events for experiments that have not been activated AND to signal
// cleanup.
const _experimentActivatedTracker = {};

// Wrap vendors/optimizely ExperimentationProvider with WB analytics
export const ExperimentationProvider = ({ children }) => {
  onExperimentActivated(trackExperimentActivated);
  onExperimentVariationViewed(trackExperimentVariationViewed);

  return (
    <OptimizelyExperimentationProvider>
      {children}
    </OptimizelyExperimentationProvider>
  );
};

// Note: these analytics are a bit non-intuitive because they are meant to match
// the RemoteConfig A/B testing system.
async function trackExperimentActivated({ experiment, variables, variation }) {
  _experimentActivatedTracker[experiment] = variation;
  const groupId = `experiment-${experiment}-panel-${variation}`;
  const params = getTrackingParams({ experiment, variables, variation });
  await trackGroup(groupId, params);
}

async function trackExperimentVariationViewed({
  experiment,
  variables,
  variation,
}) {
  const eventName = events.AB_TEST_ENTERED;
  const params = getTrackingParams({ experiment, variables, variation });

  // if the experiment is not active, drop the event. Its probably time to declare a winner and cleanup.
  if (_experimentActivatedTracker[experiment] !== variation) {
    // eslint-disable-next-line no-console
    console.debug(
      `Experiment ${experiment} variation viewed before activated. Dropping event.`
    );
    return;
  }

  // only track the event if the variation has changed
  if (_experimentVariationViewedTracker[experiment] !== variation) {
    _experimentVariationViewedTracker[experiment] = variation;
    await trackEvent(eventName, params);
    // console.debug('Experiment Viewed', eventName, experiment);
  }
}

function getTrackingParams({ experiment, variables, variation }) {
  const params = {
    experiment,
    panel: variation,
    value: null,
    prior_value: null,
    parameter_name: null,
    is_active: true,
  };

  const variableNames = Object.keys(variables || {});
  if (variableNames.length > 0) {
    params.parameter_name = variableNames.shift();
    params.value = variables[params.parameter_name];
    params.prior_value = variables[params.parameter_name];
  }

  return params;
}
